// @ts-strict-ignore
import { isNil } from 'lodash';
import React, { useCallback, useContext, useEffect } from 'react';
import { View } from '@shared/components/View';
import { LinkedCard } from '@shared/components/blocks/lib/cards';
import {
  useCardChannel,
  useQuickCreateCard,
  useSearchCards,
} from '@shared/components/blocks/lib/cards/hooks';
import { CardSelectItem } from '@shared/components/blocks/lib/inputCards/cardSelectItem';
import { NoCards } from '@shared/components/blocks/lib/inputCards/noCards';
import { QuickCreateCard } from '@shared/components/blocks/lib/inputCards/quickCreateCard';
import { SearchEmpty } from '@shared/components/blocks/lib/inputCards/searchEmpty';
import { Fields } from '@shared/components/blocks/types';
import { BottomSheetContext } from '@shared/components/bottomSheet';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBMultiSelect } from '@shared/components/multiSelect';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import { getTestId } from '@shared/util/getTestId';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useSearchWorkflowEntities } from '../../cards/useSearchWorkflowEntities';

interface SearchHooks {
  keyword: string;
  onSearch(keyword: string): void;
  onAdd(card: MBAPI.Card): void;
  searchResults: LinkedCard[];
  reset(): void;
  loading: boolean;
  hasMore: boolean;
}

interface Props {
  values: LinkedCard[];
  fieldValues?: Fields;
  onChange(cards: LinkedCard[]): void;
  fromChannelId?: number;
  relatedCardComponent?: RelatedCardComponent;
  max?: number;
  quickCreate?: boolean;
}

export function SelectCards(props: Props) {
  if (!isNil(props.relatedCardComponent)) {
    return <SelectCardsFilterSort {...props} />;
  } else {
    return <SelectCardsLegacy {...props} />;
  }
}

function SelectCardsLegacy(props: Props) {
  const hooks = useSearchCards(props.fromChannelId, props.relatedCardComponent);
  return <SelectCardsInner {...props} hooks={hooks} />;
}

export function SelectCardsFilterSort(props: Props) {
  const hooks = useSearchWorkflowEntities(props.relatedCardComponent, props.fieldValues);
  return <SelectCardsInner {...props} hooks={hooks} />;
}

function SelectCardsInner({
  values: selectedValues,
  onChange,
  max,
  fromChannelId,
  quickCreate = true,
  hooks,
}: Props & { hooks: SearchHooks }) {
  const { onSearch, onAdd, searchResults, loading, hasMore } = hooks;

  const isDesktop = useIsDesktop();

  const bottomSheetContext = useContext(BottomSheetContext);
  useEffect(() => {
    if (isDesktop) {
      bottomSheetContext.refreshHeight?.();
    }
  }, [bottomSheetContext, isDesktop, searchResults]);

  const renderItem = useCallback(
    (card: LinkedCard, index: number) => (
      <CardSelectItem card={card} testID={getTestId('card-select-item', index)} />
    ),
    [],
  );

  const renderEmptyState = useCallback(
    (query: string) => {
      return (
        <View paddingVertical={3} paddingHorizontal={2} center>
          {query.length > 0 && !loading && <SearchEmpty keyword={query} />}
          {query.length === 0 && !loading && <NoCards />}
        </View>
      );
    },
    [loading],
  );

  const fromChannel = useCardChannel(fromChannelId);
  const channelName = fromChannel?.name;
  const createCard = useQuickCreateCard(fromChannel);
  const createAndSelectCard = useCallback(
    async (name: string) => {
      const card = await createCard(name);
      const linkedCard: LinkedCard = { cardId: card.id, name: card.fields.name };
      onChange([...selectedValues, linkedCard]);
      onAdd(card);
    },
    [createCard, onAdd, selectedValues, onChange],
  );

  const renderCreateNew = useCallback(
    (query: string, creating: boolean) => {
      if (creating) {
        return <MBLoadingIndicator padding={4} />;
      }
      return <QuickCreateCard channelName={channelName} query={query} />;
    },
    [channelName],
  );

  const match = useCallback((card: LinkedCard, query: string, exact: boolean) => {
    if (exact) {
      return card.name === query;
    }
    return true;
  }, []);

  return (
    <MBMultiSelect<LinkedCard>
      onChange={onChange}
      onCreate={channelName && quickCreate ? createAndSelectCard : undefined}
      renderCreateNew={quickCreate ? renderCreateNew : undefined}
      keyExtractor={card => card.cardId}
      max={max}
      match={match}
      renderItem={renderItem}
      renderEmptyState={renderEmptyState}
      values={searchResults}
      selectedValues={selectedValues}
      onSearch={onSearch}
      helpText={hasMore ? i18n('card.blocks.cardsInput.cardListLimitHelp') : undefined}
      displaySelected
      loading={loading}
    />
  );
}
